import {useAlive} from '@github-ui/use-alive'
import {throttle} from '@github/mini-throttle'
import useIsMounted from '@github-ui/use-is-mounted'
import {useCallback, useMemo} from 'react'
// The event names that can be watched for in the alive message - add new events here
export const PR_ALIVE_EVENT_NAMES = [
  'git_updated',
  'title_updated',
  'body_updated',
  'timeline_updated',
  'sidebar_updated',
  'assignees_updated',
  'reviewers_updated',
  'labels_updated',
  'issue_references_updated',
  'tab_counts_updated',
  'findings_updated',
] as const

// Maps PR_ALIVE_EVENT_NAMES to a union of the event names
type EventNames = (typeof PR_ALIVE_EVENT_NAMES)[number]

// Strongly type the event updates that can be watched for in the alive message
export type EventUpdate = {
  [key in EventNames]?: boolean
}

// Shape of the alive notification payload published on the PR channel.
// `event_context` carries per-event reconciliation hints (e.g. the
// description's body version) so consumers can dedupe against state they
// already have without an extra fetch.
export type PullRequestAliveData = {
  wait?: number
  event_updates?: EventUpdate
  event_context?: PullRequestAliveContext
}

export type PullRequestAliveContext = {
  description_body_version?: string
}

/**
 * Refetches data on alive updates for a given channel
 * Syntactic sugar for useRespondToAliveUpdate for consumer clarity
 */
export function useRefetchOnAliveUpdate(
  channel: string | null | undefined,
  refetch: () => void,
  watchFor?: EventUpdate,
  throttleTimeout?: number,
) {
  // Wrap to discard the alive `event_context` arg passed by useRespondToAliveUpdate's
  // handler signature, and to keep the reference stable so throttledHandler isn't
  // rebuilt on every render.
  const handler = useCallback(() => refetch(), [refetch])
  useRespondToAliveUpdate(channel, handler, watchFor, throttleTimeout)
}

/**
 * Respond to alive updates on a given channel.
 *
 * The handler may optionally accept the alive `event_context` to reconcile
 * state without refetching (e.g. compare a body version).
 */
export function useRespondToAliveUpdate(
  channel: string | null | undefined,
  handler: (context?: PullRequestAliveContext) => void,
  watchFor?: EventUpdate,
  throttleTimeout?: number,
): void {
  const isMounted = useIsMounted()

  const throttledHandler = useMemo(
    () =>
      throttle((context: PullRequestAliveContext | undefined) => {
        if (isMounted()) {
          handler(context)
        }
      }, throttleTimeout ?? 2000),
    [isMounted, handler, throttleTimeout],
  )

  const handleNotification = useCallback(
    (data: PullRequestAliveData) => {
      if (watchFor && data.event_updates) {
        const watchForEntries = Object.entries(watchFor) as Array<[EventNames, boolean]>
        for (const [key, value] of watchForEntries) {
          if (!!data.event_updates[key] === !!value) {
            window.setTimeout(() => throttledHandler(data.event_context), data.wait || 0)
          }
        }
      } else {
        // If we're not watching for anything specific, just refetch
        window.setTimeout(() => throttledHandler(data.event_context), data.wait || 0)
      }
    },
    [throttledHandler, watchFor],
  )

  useAlive(channel, handleNotification)
}
